/**
 * Atelier Mino — aperçu du prénom sur la vraie photo du produit.
 *
 * Pour les produits dont les variantes ont une image « sans prénom » (métachamp de variante mino.apercu) et une
 * zone calibrée (mino.zone_prenom, scripts/calibrer_zone_prenom.py) :
 *   - galerie : dès que la cliente tape, l'image de la variante sélectionnée est remplacée par sa version sans
 *     prénom et le prénom saisi est posé par-dessus, à la zone calibrée, dans la police choisie ;
 *     champ vide : image d'origine (avec le prénom d'exemple) ;
 *   - mobile : copie réduite du même aperçu sous le champ (bloc mino-gravure).
 * Taille : « Zoé » (mot étalon) occupe exactement la largeur mesurée ; un prénom plus long rétrécit pour ne pas
 * dépasser le séparateur. La galerie est reconstruite par Horizon à chaque changement de coloris : un
 * MutationObserver réapplique l'aperçu.
 * Position (06/10/2026) : si la configuration porte des positions (métachamp produit mino.positions) et que la cliente en
 * choisit une autre que la position par défaut, le texte est posé dans la zone de cette position (même taille de lettres,
 * aligné à gauche, au centre ou à droite, rétréci s'il dépasse la zone) ; champ vide : le prénom d'exemple y est déplacé.
 * Gamme prénom (08/10/2026, configuration avec « ornements ») : le champ arrive prérempli avec le prénom de la fiche ; tant
 * que prénom, ornement de la fiche et date vide correspondent à la photo, la photo d'origine reste affichée. Sinon l'image est
 * composée : aperçu sans prénom (ornement 1) ou fond vierge du coloris (mino.fond) + PNG de l'ornement choisi, prénom saisi,
 * date (zone.date : centre, ligne de base et taille du générateur). Ornement et date sont dans un groupe à part du calque :
 * le prénom reste son premier enfant (mino-animations.js y pose le laser). Au choix d'un ornement, le carrousel (mobile)
 * revient sur la grande image.
 * Lots de 2 porte-clés (12/10/2026) : si la zone de la variante porte une 2e zone (mino.zone_prenom → « zone2 », même forme :
 * x0, x1, y0, y1, cx, sep_x0, sep_x1, mot_etalon ; couleur et mode hérités), le texte du 2e champ est posé dans le même
 * calque, en 2e élément <text> (classe mino-surimpression__t2). Dès qu'un des deux champs est rempli, l'image passe à
 * l'aperçu sans les deux mots ; le champ resté vide montre son mot d'exemple. Sans « zone2 », rien ne change.
 * Verso (13/10/2026, porte-clés) : une variante « Recto et verso » porte « verso » (image du dos sans texte, mino.verso) et sa
 * zone « verso » (même forme, « zone2 » pour un lot ; couleur et mode hérités). Le bloc du verso (assets/mino-gravure.js,
 * activerVerso) est activé ou non à chaque variante, et l'aperçu du dos est composé sous ses champs (mot d'exemple si vide).
 * Bodies au prénom (13/10/2026, configuration avec « body ») : comme la gamme prénom, mais l'image est toujours composée sur le fond
 * vierge (mino.fond) ; l'ornement est un PNG blanc servant de masque à un aplat de la couleur du flex choisie ([data-mino-couleur] :
 * « #teinte » ou « paillettes ») ; prénom, ornement et date prennent cette couleur ; la photo d'origine reste affichée tant que
 * prénom, ornement, couleur de la fiche et date vide correspondent à la photo.
 * Cœur en fin de ligne (13/10/2026, porte-clés rectangle) : si la zone porte « coeur_fin » ({ ecart, taille, xh } en em,
 * xh = hauteur d'x de la police), un petit cœur plein est dessiné juste après le texte, centré sur la hauteur d'x, de la
 * couleur de la gravure ; texte + cœur sont centrés ensemble et rétrécis ensemble s'ils dépassent la zone. Deux lignes sur
 * un porte-clés : le cœur suit la 2e ligne (zone2), ou la 1re si la 2e est vide. Sans « coeur_fin », rien ne change.
 */
(() => {
  if (window.MinoApercu) return;
  const SVG = 'http://www.w3.org/2000/svg';
  const LARGEURS = [352, 640, 832, 1200, 1600, 2048];
  const mesure = document.createElement('canvas').getContext('2d');
  let numero = 0;   // identifiants uniques des masques et motifs SVG (plusieurs calques par page)

  /** Flex pailleté : motif noir semé de paillettes argentées, défini une fois par calque ; renvoie la valeur de fill. */
  function paillettes(svg) {
    let p = svg.querySelector(':scope > defs > pattern.mino-paillettes');
    if (!p) {
      let defs = svg.querySelector(':scope > defs');
      if (!defs) { defs = document.createElementNS(SVG, 'defs'); svg.insertBefore(defs, svg.firstChild.nextSibling); }
      p = document.createElementNS(SVG, 'pattern');
      p.setAttribute('class', 'mino-paillettes');
      p.id = `mino-paillettes-${++numero}`;
      p.setAttribute('patternUnits', 'userSpaceOnUse');
      p.setAttribute('width', '1.6'); p.setAttribute('height', '1.6');
      const fond = document.createElementNS(SVG, 'rect');
      fond.setAttribute('width', '1.6'); fond.setAttribute('height', '1.6'); fond.setAttribute('fill', '#1E1E1E');
      p.appendChild(fond);
      [[0.3, 0.4, 0.07], [1.1, 0.25, 0.05], [0.8, 1.0, 0.06], [1.35, 1.3, 0.045], [0.15, 1.25, 0.05]].forEach(([x, y, r]) => {
        const c = document.createElementNS(SVG, 'circle');
        c.setAttribute('cx', x); c.setAttribute('cy', y); c.setAttribute('r', r); c.setAttribute('fill', '#D7D7DE');
        p.appendChild(c);
      });
      defs.appendChild(p);
    }
    return `url(#${p.id})`;
  }

  function url(base, largeur) {
    return `${base}${base.includes('?') ? '&' : '?'}width=${largeur}`;
  }

  /** Police réellement appliquée par le thème (classes mino-police--*) : une seule source de vérité, le CSS. */
  function stylePolice(racine, police) {
    let sonde = racine.querySelector(`[data-mino-sonde="${police}"]`);
    if (!sonde) {
      sonde = document.createElement('span');
      sonde.dataset.minoSonde = police;
      sonde.className = `mino-police--${police}`;
      sonde.setAttribute('aria-hidden', 'true');
      sonde.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;font-size:100px;white-space:nowrap;';
      sonde.textContent = 'Zoé';
      racine.appendChild(sonde);
    }
    const cs = getComputedStyle(sonde);
    const espacement = parseFloat(cs.letterSpacing);
    return {
      famille: cs.fontFamily,
      graisse: cs.fontWeight,
      majuscules: cs.textTransform === 'uppercase',
      espacementEm: Number.isFinite(espacement) ? espacement / 100 : 0,
    };
  }

  /** Encre réelle d'un texte (largeur, débord à gauche, descente) pour une police donnée, à 100 px. */
  function encre(texte, st) {
    mesure.font = `${st.graisse} 100px ${st.famille}`;
    if ('letterSpacing' in mesure) mesure.letterSpacing = `${st.espacementEm * 100}px`;
    const m = mesure.measureText(texte);
    return { largeur: m.actualBoundingBoxLeft + m.actualBoundingBoxRight, gauche: m.actualBoundingBoxLeft, descente: m.actualBoundingBoxDescent };
  }

  /** Pose (ou met à jour) le prénom dans un calque SVG 0–100 × 0–100 superposé à l'image carrée.
   *  pos (facultatif) : zone d'une position choisie { x0, x1, y0, y1, align } ; sinon la zone calibrée de la variante. */
  function dessiner(cadre, zone, texte, st, pos = null, deco = null, texte2 = null) {
    let svg = cadre.querySelector(':scope > .mino-surimpression');
    if (!svg) {
      svg = document.createElementNS(SVG, 'svg');
      svg.setAttribute('class', 'mino-surimpression');
      svg.setAttribute('viewBox', '0 0 100 100');
      // images carrées affichées en object-fit: cover (cadre parfois moins haut que large) : le calque suit le même recadrage
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      svg.setAttribute('aria-hidden', 'true');
      svg.appendChild(document.createElementNS(SVG, 'text'));
      cadre.appendChild(svg);
    }
    // body : couleur du flex choisie (teinte ou paillettes) pour le prénom, l'ornement et la date
    if (deco?.couleur) zone = { ...zone, couleur: deco.couleur === 'paillettes' ? paillettes(svg) : deco.couleur };
    // deux lignes sur un porte-clés, la 2e (facultative) vide : la 1re descend au milieu des deux lignes (13/10/2026)
    const seule = zone.zone2 && texte2 === '' && !pos;
    placer(svg.firstChild, seule ? { ...zone, y1: (zone.y1 + zone.zone2.y1) / 2, coeur_fin: zone.coeur_fin || zone.zone2.coeur_fin } : zone, texte, st, pos);
    // lot de 2 : 2e texte dans sa zone (couleur et mode de la 1re), juste après le 1er
    let t2 = svg.querySelector(':scope > text.mino-surimpression__t2');
    if (zone.zone2 && texte2 != null) {
      if (!t2) {
        t2 = document.createElementNS(SVG, 'text');
        t2.setAttribute('class', 'mino-surimpression__t2');
        svg.insertBefore(t2, svg.firstChild.nextSibling);
      }
      placer(t2, { couleur: zone.couleur, opacite: zone.opacite, ...zone.zone2 }, texte2, st, null);
    } else { t2?.remove(); svg.querySelector(':scope > path[data-coeur-de="2"]')?.remove(); }
    svg.style.mixBlendMode = zone.mode === 'normal' ? 'normal' : 'multiply';
    decorer(svg, zone, st, deco);
  }

  /** Place un texte dans sa zone : « mot étalon » à la largeur mesurée, rétréci s'il dépasse, même ligne de base. */
  function placer(t, zone, texte, st, pos) {
    const visible = st.majuscules ? texte.toLocaleUpperCase('fr') : texte;
    const etalon = encre(st.majuscules ? zone.mot_etalon.toLocaleUpperCase('fr') : zone.mot_etalon, st);
    let k = (zone.x1 - zone.x0) / etalon.largeur;            // unités du calque par px à 100 px
    const ligne = (pos ? pos.y1 : zone.y1) - etalon.descente * k;   // ligne de base : celle de « Zoé » sur la photo
    const e = encre(visible, st);
    const max = pos ? pos.x1 - pos.x0 : zone.sep_x1 - zone.sep_x0;
    const cf = !pos && zone.coeur_fin;                         // cœur en fin de ligne : (écart + cœur) en em, em = 100 px
    const queue = cf && visible ? (cf.ecart + cf.taille) * 100 : 0;
    if ((e.largeur + queue) * k > max) k = max / (e.largeur + queue);   // prénom long : on rétrécit, même ligne de base
    const largeur = e.largeur * k;
    let debut = zone.cx - (largeur + queue * k) / 2;         // centré sur la zone calibrée (avec le cœur s'il y en a un)
    coeurFin(t, cf && visible ? { x: debut + largeur + cf.ecart * 100 * k, y: ligne - cf.xh * 100 * k / 2,
                                  l: cf.taille * 100 * k, couleur: zone.couleur, opacite: zone.opacite } : null);
    if (pos) {
      if (pos.align === 'gauche') debut = pos.x0;
      else if (pos.align === 'droite') debut = pos.x1 - largeur;
      else debut = (pos.x0 + pos.x1) / 2 - largeur / 2;
    }
    t.textContent = visible;
    t.setAttribute('x', (debut + e.gauche * k).toFixed(3));
    t.setAttribute('y', ligne.toFixed(3));
    t.setAttribute('font-size', (100 * k).toFixed(3));
    t.setAttribute('fill', zone.couleur);
    t.setAttribute('fill-opacity', zone.opacite ?? 1);
    t.style.fontFamily = st.famille;
    t.style.fontWeight = st.graisse;
    t.style.letterSpacing = `${st.espacementEm}em`;
  }

  /** Cœur plein après un texte (zone « coeur_fin ») : <path> frère du <text>, repéré par data-coeur-de ; c = { x (bord gauche),
   *  y (centre), l (largeur), couleur, opacite } ou null pour l'enlever. */
  function coeurFin(t, c) {
    const svg = t.parentNode;
    const cle = t.classList.contains('mino-surimpression__t2') ? '2' : '1';
    let p = svg?.querySelector(`:scope > path[data-coeur-de="${cle}"]`);
    if (!c) { p?.remove(); return; }
    if (!p) {
      p = document.createElementNS(SVG, 'path');
      p.setAttribute('data-coeur-de', cle);
      svg.insertBefore(p, t.nextSibling);
    }
    const s = c.l / 2, cx = c.x + s, pts = [];
    for (let i = 0; i <= 60; i++) {
      const u = (i / 60) * 2 * Math.PI;
      pts.push(`${(cx + 16 * Math.sin(u) ** 3 * s / 16).toFixed(3)},${(c.y - (13 * Math.cos(u) - 5 * Math.cos(2 * u) - 2 * Math.cos(3 * u) - Math.cos(4 * u)) * s / 16).toFixed(3)}`);
    }
    p.setAttribute('d', `M${pts.join('L')}Z`);
    p.setAttribute('fill', c.couleur);
    p.setAttribute('fill-opacity', c.opacite ?? 1);
  }

  /** Gamme prénom : ornement (PNG gris, posé en multiply comme le prénom) et date, dans un groupe après le prénom. */
  function decorer(svg, zone, st, deco) {
    let g = svg.querySelector(':scope > g.mino-surimpression__deco');
    if (!deco || (!deco.ornement && !deco.date)) { g?.remove(); return; }
    if (deco.ornement?.masque) return decorerMasque(svg, g, zone, st, deco);
    if (g?.dataset.masque) { g.remove(); g = null; }
    if (!g) {
      g = document.createElementNS(SVG, 'g');
      g.setAttribute('class', 'mino-surimpression__deco');
      g.appendChild(document.createElementNS(SVG, 'image'));
      g.appendChild(document.createElementNS(SVG, 'text'));
      svg.appendChild(g);
    }
    const [img, date] = g.children;
    if (deco.ornement) {
      const c = deco.ornement.cadre;
      if (img.getAttribute('href') !== deco.ornement.png) img.setAttribute('href', deco.ornement.png);
      img.setAttribute('x', c.x); img.setAttribute('y', c.y); img.setAttribute('width', c.l); img.setAttribute('height', c.h);
      img.setAttribute('preserveAspectRatio', 'none');
      img.removeAttribute('display');
    } else img.setAttribute('display', 'none');
    const d = zone.date;
    if (deco.date && d) {
      date.textContent = deco.date;
      date.setAttribute('x', d.cx); date.setAttribute('y', d.ligne); date.setAttribute('font-size', d.taille);
      date.setAttribute('text-anchor', 'middle');          // centre de la chasse, comme l'ancre « ms » du générateur
      date.setAttribute('fill', zone.couleur);
      date.style.fontFamily = st.famille;
      date.style.fontWeight = st.graisse;
      date.removeAttribute('display');
    } else date.setAttribute('display', 'none');
  }

  /** Body : ornement = aplat de la couleur du flex découpé par le PNG blanc (masque SVG), puis date de la même couleur. */
  function decorerMasque(svg, g, zone, st, deco) {
    if (!g?.dataset.masque) {
      g?.remove();
      g = document.createElementNS(SVG, 'g');
      g.setAttribute('class', 'mino-surimpression__deco');
      g.dataset.masque = '1';
      const masque = document.createElementNS(SVG, 'mask');
      masque.id = `mino-masque-${++numero}`;
      masque.appendChild(document.createElementNS(SVG, 'image'));
      const aplat = document.createElementNS(SVG, 'rect');
      aplat.setAttribute('mask', `url(#${masque.id})`);
      g.append(masque, aplat, document.createElementNS(SVG, 'text'));
      svg.appendChild(g);
    }
    const [masque, aplat, date] = g.children;
    const img = masque.firstChild;
    const c = deco.ornement.cadre;
    if (img.getAttribute('href') !== deco.ornement.png) img.setAttribute('href', deco.ornement.png);
    [img, aplat].forEach((el) => { el.setAttribute('x', c.x); el.setAttribute('y', c.y); el.setAttribute('width', c.l); el.setAttribute('height', c.h); });
    img.setAttribute('preserveAspectRatio', 'none');
    masque.setAttribute('maskUnits', 'userSpaceOnUse');
    masque.setAttribute('x', c.x); masque.setAttribute('y', c.y); masque.setAttribute('width', c.l); masque.setAttribute('height', c.h);
    aplat.setAttribute('fill', zone.couleur);
    const d = zone.date;
    if (deco.date && d) {
      date.textContent = deco.date;
      date.setAttribute('x', d.cx); date.setAttribute('y', d.ligne); date.setAttribute('font-size', d.taille);
      date.setAttribute('text-anchor', 'middle');
      date.setAttribute('fill', zone.couleur);
      date.style.fontFamily = st.famille;
      date.style.fontWeight = st.graisse;
      date.removeAttribute('display');
    } else date.setAttribute('display', 'none');
  }

  function srcsetGalerie(base) {
    return LARGEURS.map((l) => `${url(base, l)} ${l}w`).join(', ');
  }

  /** Fondu d'apparition : le cadre reste masqué (classe mino-attente) jusqu'à ce que l'image soit décodée. */
  function reveler(cadre, img) {
    const jeton = (cadre.minoJeton = (cadre.minoJeton || 0) + 1);
    const fin = () => { if (cadre.minoJeton === jeton) cadre.classList.remove('mino-attente'); };
    if (!img) return fin();
    const pret = () => (img.decode ? img.decode() : Promise.resolve()).then(fin, fin);
    if (img.complete && img.naturalWidth) pret();                    // déjà en cache (préchargée)
    else {
      img.addEventListener('load', pret, { once: true });
      img.addEventListener('error', fin, { once: true });
    }
    setTimeout(fin, 2500); // filet de sécurité : jamais d'image masquée durablement
  }

  function remplacerImage(img, base) {
    if (!img.dataset.minoSrc) {
      img.dataset.minoSrc = img.getAttribute('src') || '';
      img.dataset.minoSrcset = img.getAttribute('srcset') || '';
    }
    const srcset = srcsetGalerie(base);
    if (img.getAttribute('srcset') !== srcset) {
      img.setAttribute('srcset', srcset);
      img.setAttribute('src', url(base, 1600));
    }
  }

  function restaurer(cadre) {
    cadre.querySelector(':scope > .mino-surimpression')?.remove();
    cadre.classList.remove('mino-apercu-actif', 'mino-attente');
    delete cadre.dataset.minoCle;
    const img = cadre.querySelector('img');
    if (img?.dataset.minoSrc !== undefined) {
      img.setAttribute('src', img.dataset.minoSrc);
      if (img.dataset.minoSrcset) img.setAttribute('srcset', img.dataset.minoSrcset);
      else img.removeAttribute('srcset');
      delete img.dataset.minoSrc;
      delete img.dataset.minoSrcset;
    }
  }

  class Apercu {
    constructor(racine) {
      this.racine = racine;
      this.cfg = JSON.parse(racine.querySelector('[data-mino-apercu-config]').textContent);
      this.section = racine.closest('.shopify-section') || document;
      this.champs = [...racine.querySelectorAll('[data-mino-champ]')];
      this.champ = this.champs[0];
      this.mini = racine.querySelector('[data-mino-apercu-photo]');
      racine.addEventListener('input', (e) => e.target.matches('[data-mino-champ], [data-mino-verso-champ]') && this.planifier());
      this.cadreVerso = racine.querySelector('[data-mino-verso-cadre]');
      racine.addEventListener('change', (e) => e.target.matches('[data-mino-police], [data-mino-position]') && this.planifier());
      if (this.cfg.ornements) {
        racine.addEventListener('change', (e) => {
          if (!e.target.matches('[data-mino-ornement], [data-mino-couleur]')) return;
          this.planifier();
          // mobile (carrousel) : retour sur la grande image, qui montre l'ornement choisi ; sans effet sur la grille
          this.section.querySelector('media-gallery')?.slideshow?.select?.(0);
        });
        racine.addEventListener('input', (e) => e.target.matches('[data-mino-date]') && this.planifier());
      }
      // Le rappel d'un MutationObserver passe avant que le navigateur ne redessine : la nouvelle galerie (après un
      // changement de coloris) est masquée tout de suite, puis révélée en fondu une fois la bonne image prête.
      new MutationObserver(() => { this.masquerNouvelles(); this.planifier(); })
        .observe(this.section, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      this.prechargees = new Set();
      // images présentes au chargement de la page : pas de fondu (il retarderait l'image principale, LCP)
      this.section.querySelectorAll('media-gallery .product-media[data-media-id]').forEach((c) => { c.dataset.minoVu = '1'; });
      document.fonts?.addEventListener?.('loadingdone', () => this.planifier(true));
      this.planifier();
    }

    variante() {
      const form = document.getElementById(this.racine.dataset.form);
      const id = form?.querySelector('[name="id"]')?.value || new URL(location.href).searchParams.get('variant');
      return this.cfg.variantes[id] ? id : this.cfg.defaut;
    }

    texte(i = 0) {
      return (this.champs[i]?.value || '').replace(/\s+/g, ' ').trim();
    }

    /** Lot de 2 : texte du 2e porte-clés (mot d'exemple si vide) ; null sans 2e zone. Deux lignes sur un porte-clés
     *  (13/10/2026) : 2e ligne facultative laissée vide alors que la 1re est saisie = '' (rien sur la 2e ligne). */
    texte2(v) {
      if (!v?.zone?.zone2) return null;
      if (!this.texte(1) && this.texte() && this.champs[1]?.hasAttribute('data-mino-facultatif')) return '';
      return this.texte(1) || v.zone.zone2.mot_etalon;
    }

    /** Gamme prénom : ornement choisi et date saisie ; null pour les autres produits. */
    deco() {
      const orn = this.cfg.ornements;
      if (!orn) return null;
      const n = Number(this.racine.querySelector('[data-mino-ornement]:checked')?.dataset.minoOrnement || 1);
      const o = orn.find((x) => x.n === n) || orn[0];
      const date = (this.racine.querySelector('[data-mino-date]')?.value || '').replace(/[^\d/]/g, '').trim();
      if (this.cfg.body) {
        const c = this.racine.querySelector('[data-mino-couleur]:checked');
        return { n: o.n, ornement: { png: o.png, cadre: this.cfg.cadre_ornement, masque: true }, date,
          couleur: c?.dataset.minoCouleur || null, couleurNom: c?.value || this.cfg.couleur_fiche };
      }
      return { n: o.n, ornement: o.n === 1 ? null : { png: o.png, cadre: this.cfg.cadre_ornement }, date };
    }

    /** Texte à poser sur la photo ; '' = photo d'origine. Gamme prénom : la photo porte déjà le prénom de la fiche avec
     *  l'ornement de la fiche (cfg.ornement_fiche) et sans date ; tout autre choix compose l'image. */
    affichage(v, deco) {
      const texte = this.texte();
      // lot de 2 : un seul des deux champs rempli suffit pour passer à l'aperçu (l'autre montre son mot d'exemple)
      if (v?.zone?.zone2) return texte || this.texte(1) ? (texte || v.zone.mot_etalon) : '';
      if (!deco || !v) return texte || (this.position() && v ? v.zone.mot_etalon : '');
      const nom = texte || v.zone.mot_etalon;
      const memeCouleur = !this.cfg.body || deco.couleurNom === this.cfg.couleur_fiche;
      return nom === v.zone.mot_etalon && deco.n === (this.cfg.ornement_fiche || 1) && !deco.date && memeCouleur ? '' : nom;
    }

    /** Image de base : fond vierge du coloris si l'ornement n'est pas celui de l'aperçu, sinon l'aperçu sans prénom. */
    base(v, deco) {
      return (deco?.ornement || this.cfg.body) && v.fond ? v.fond : v.apercu;
    }

    /** Position choisie, sauf la position par défaut (zone calibrée de la variante) : null. */
    position() {
      const conf = this.cfg.positions;
      if (!conf?.positions?.length) return null;
      const nom = this.racine.querySelector('[data-mino-position]:checked')?.value || conf.defaut;
      if (nom === conf.defaut) return null;
      return conf.positions.find((p) => p.nom === nom) || null;
    }

    /** Images de galerie apparues depuis le dernier passage : masquées jusqu'à ce que la bonne image soit prête. */
    masquerNouvelles() {
      const v = this.cfg.variantes[this.variante()];
      const texte = this.affichage(v, this.deco());   // position choisie : aperçu même champ vide
      this.section.querySelectorAll('media-gallery .product-media[data-media-id]').forEach((cadre) => {
        if (cadre.dataset.minoVu || cadre.closest('dialog')) return;
        cadre.dataset.minoVu = '1';
        const img = cadre.querySelector('img');
        if (texte && v && cadre.dataset.mediaId === String(v.media)) {
          cadre.classList.add('mino-attente');            // révélée par appliquer(), sur l'image sans prénom
        } else if (img && !img.complete) {
          cadre.classList.add('mino-attente');            // image d'origine : fondu à la fin du chargement
          reveler(cadre, img);
        }
      });
    }

    /** Dès la première lettre : précharge les images sans prénom de tous les coloris (même srcset et sizes que la galerie). */
    precharger() {
      const ref = this.section.querySelector('media-gallery .product-media img');
      const avecFonds = Boolean(this.deco()?.ornement);
      Object.values(this.cfg.variantes).forEach((v) => {
        [v.apercu, avecFonds ? v.fond : null].forEach((src) => {
          if (!src || this.prechargees.has(src)) return;
          this.prechargees.add(src);
          const im = new Image();
          im.decoding = 'async';
          if (ref?.getAttribute('sizes')) im.sizes = ref.getAttribute('sizes');
          im.srcset = srcsetGalerie(src);
          const mini = new Image();
          mini.sizes = '240px';
          mini.srcset = `${url(src, 360)} 360w, ${url(src, 640)} 640w`;
        });
      });
    }

    planifier(force = false) {
      if (force) this.force = true;
      // délai court plutôt que requestAnimationFrame : ce dernier est suspendu quand l'onglet est en arrière-plan
      clearTimeout(this.minuterie);
      this.minuterie = setTimeout(() => this.appliquer(), 30);
    }

    async appliquer() {
      const id = this.variante();
      const v = this.cfg.variantes[id];
      this.racine.activerVerso?.(Boolean(v?.verso && v.zone?.verso));
      const texte = this.texte();
      const pos = this.position();
      const deco = this.deco();
      // position choisie et champ vide : le prénom d'exemple est déplacé à cette position (image sans prénom) ;
      // gamme prénom : voir affichage()
      const affiche = this.affichage(v, deco);
      const base = v ? this.base(v, deco) : null;
      if (affiche) this.precharger();
      const police = this.racine.querySelector('[data-mino-police]:checked')?.dataset.police || this.cfg.police;
      const st = stylePolice(this.racine, police);
      try { await document.fonts?.load(`${st.graisse} 100px ${st.famille}`, affiche || v?.zone.mot_etalon); } catch (e) { /* police de repli */ }
      const texte2 = this.texte2(v);
      const cle = `${id}|${affiche}|${texte2 ?? ''}|${police}|${pos ? pos.nom : ''}|${deco ? `${deco.n}|${deco.date}|${deco.couleur || ''}` : ''}`;
      const force = this.force;
      this.force = false;

      // Galerie : seule l'image de la variante sélectionnée porte l'aperçu, et seulement quand un prénom est saisi
      this.section.querySelectorAll('media-gallery .product-media[data-media-id]').forEach((cadre) => {
        if (cadre.closest('dialog')) return;
        const cible = v && affiche && cadre.dataset.mediaId === String(v.media);
        if (!cible) {
          if (cadre.dataset.minoCle) restaurer(cadre);
          return;
        }
        if (cadre.dataset.minoCle === cle && !force) return;
        cadre.dataset.minoCle = cle;
        cadre.classList.add('mino-apercu-actif');
        const img = cadre.querySelector('img');
        const avant = img.getAttribute('srcset');
        remplacerImage(img, base);
        dessiner(cadre, v.zone, affiche, st, pos, deco, texte2);
        // image changée (premier prénom, nouveau coloris) : fondu ; simple frappe au clavier : rien à attendre
        if (img.getAttribute('srcset') !== avant) cadre.classList.add('mino-attente');
        if (cadre.classList.contains('mino-attente')) reveler(cadre, img);
      });

      // Mobile : copie réduite sous le champ (prénom d'exemple tant que le champ est vide)
      if (this.mini && v && (this.mini.dataset.minoCle !== cle || force)) {
        this.mini.dataset.minoCle = cle;
        const img = this.mini.querySelector('img');
        const src = url(base, 640);
        const cadreMini = this.mini.querySelector('.mino-gravure__apercu-cadre');
        if (img.getAttribute('src') !== src) {
          if (img.getAttribute('src')) cadreMini.classList.add('mino-attente');
          img.setAttribute('src', src);
          img.setAttribute('srcset', `${url(base, 360)} 360w, ${src} 640w`);
          reveler(cadreMini, img);
        }
        dessiner(cadreMini, v.zone, texte || v.zone.mot_etalon, st, pos, deco, texte2);
      }

      // Verso : dos du coloris + texte(s) du verso (mot d'exemple tant que le champ est vide), sous ses champs
      if (this.cadreVerso && v?.verso && v.zone?.verso) {
        const tv = [...this.racine.querySelectorAll('[data-mino-verso-champ]')].map((c) => c.value.replace(/\s+/g, ' ').trim());
        const zv = { couleur: v.zone.couleur, mode: v.zone.mode, opacite: v.zone.opacite, ...v.zone.verso };
        const img = this.cadreVerso.querySelector('img');
        const src = url(v.verso, 640);
        if (img.getAttribute('src') !== src) {
          if (img.getAttribute('src')) this.cadreVerso.classList.add('mino-attente');
          img.setAttribute('src', src);
          img.setAttribute('srcset', `${url(v.verso, 360)} 360w, ${src} 640w`);
          reveler(this.cadreVerso, img);
        }
        dessiner(this.cadreVerso, zv, tv[0] || zv.mot_etalon, st, null, null, zv.zone2 ? (tv[1] || zv.zone2.mot_etalon) : null);
      }
    }
  }

  function demarrer() {
    document.querySelectorAll('mino-gravure[data-apercu-photo]').forEach((el) => {
      if (!el.minoApercu) el.minoApercu = new Apercu(el);
    });
  }
  window.MinoApercu = { demarrer };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})();
